import FileDownloadIcon from '@mui/icons-material/FileDownload';
import FilterListIcon from '@mui/icons-material/FilterList';
import NoteAddOutlinedIcon from '@mui/icons-material/NoteAddOutlined';
import ViewColumnIcon from '@mui/icons-material/ViewColumn';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import { Badge, Button, Menu, MenuItem } from '@mui/material';
import { Box } from '@mui/system';
import {
  ColumnsPanelTrigger,
  ExportCsv,
  ExportPrint,
  FilterPanelTrigger,
  GridToolbarProps,
  Toolbar,
  useGridRootProps,
} from '@mui/x-data-grid-pro';
import { ReactNode, useRef, useState } from 'react';

import useAuth from 'portal/src/auth/useAuth';

export interface DeactivateConfig {
  isChecked: boolean;
  onToggleCheckbox: (show: boolean) => void;
}

export interface CustomToolbarProps extends GridToolbarProps {
  children?: ReactNode;
  deactivateConfig?: DeactivateConfig;
  exportFileName?: string;
  onNewButtonClick?: () => void | Promise<void>;
  leftSideChildren?: ReactNode;
  showColumns?: boolean;
  showFilters?: boolean;
}

export function CustomToolbar({
  children,
  deactivateConfig,
  exportFileName,
  leftSideChildren = null,
  onNewButtonClick = null,
  showColumns = false,
  showFilters = true,
}: CustomToolbarProps) {
  const { hasReadOnlyAccess } = useAuth();
  const triggerRef = useRef<HTMLButtonElement>(null);
  const gridProps = useGridRootProps();
  const isReadonly = hasReadOnlyAccess();

  const [open, setOpen] = useState(false);

  return (
    <Toolbar>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          width: '100%',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          {showColumns && (
            <ColumnsPanelTrigger
              render={(props) => (
                <Button
                  {...props}
                  sx={{ display: 'flex', alignItems: 'center' }}
                  size="small"
                >
                  <ViewColumnIcon sx={{ fontSize: 17, mr: 0.2, mb: 0.2 }} />
                  Columns
                </Button>
              )}
            ></ColumnsPanelTrigger>
          )}
          {showFilters && (
            <FilterPanelTrigger
              render={(props, state) => (
                <Button
                  {...props}
                  disabled={gridProps.disableColumnFilter}
                  size="small"
                >
                  <Badge
                    badgeContent={state.filterCount}
                    color="primary"
                    variant="dot"
                  >
                    <FilterListIcon sx={{ fontSize: 17, mr: 0.2, mb: 0.2 }} />
                  </Badge>
                  Filters
                </Button>
              )}
            />
          )}
          {deactivateConfig && (
            <Button
              color="info"
              disabled={isReadonly}
              onClick={() =>
                deactivateConfig.onToggleCheckbox(!deactivateConfig.isChecked)
              }
              size="small"
            >
              {deactivateConfig.isChecked ? (
                <VisibilityOffIcon sx={{ fontSize: 17, mr: 0.2, mb: 0.2 }} />
              ) : (
                <VisibilityIcon sx={{ fontSize: 17, mr: 0.2, mb: 0.2 }} />
              )}
              Deactivated
            </Button>
          )}
          {exportFileName && (
            <>
              <Button
                ref={triggerRef}
                id="export-menu-trigger"
                aria-controls="export-menu"
                aria-haspopup="true"
                aria-expanded={open ? 'true' : undefined}
                onClick={() => setOpen(true)}
                color="primary"
                size="small"
              >
                <FileDownloadIcon sx={{ fontSize: 17, mr: 0.2, mb: 0.2 }} />
                Export
              </Button>
              <Menu
                id="export-menu"
                anchorEl={() => triggerRef.current}
                open={open}
                onClose={() => setOpen(false)}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                slotProps={{
                  list: {
                    'aria-labelledby': 'export-menu-trigger',
                  },
                }}
              >
                <ExportCsv
                  options={{ fileName: exportFileName }}
                  render={<MenuItem />}
                >
                  Download as CSV
                </ExportCsv>
                <ExportPrint render={<MenuItem />}>Print</ExportPrint>
              </Menu>
            </>
          )}
          {leftSideChildren}
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          {children}
          {onNewButtonClick && (
            <Button
              color="primary"
              disabled={isReadonly}
              endIcon={<NoteAddOutlinedIcon />}
              onClick={onNewButtonClick}
              variant="contained"
              size="small"
              sx={{ mx: 1 }}
            >
              New
            </Button>
          )}
        </Box>
      </Box>
    </Toolbar>
  );
}
